import React, { useState, useEffect, useCallback } from 'react'
import { UserService } from '@duck/api-definitions'
import { useQuery } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle, Tabs, TabsList, TabsTrigger, TabsContent } from '@duck/ui'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'
import { OverviewSection } from './OverviewSection'
import { AssignmentsSection } from './AssignmentsSection'
import { ChallengesSection } from './ChallengesSection'
import { SettingsSection } from './SettingsSection'
import { UserManagementDialogSkeleton } from './UserManagementDialogSkeleton'

interface UserManagementDialogProps {
  isOpen: boolean
  onClose: () => void
  userId: string | null
  onUserSaved?: () => void
  allAccessGroups: string[]
}

export const UserManagementDialog: React.FC<UserManagementDialogProps> = ({
  isOpen,
  onClose,
  userId,
  onUserSaved,
  allAccessGroups,
}) => {
  const { t } = useTar()
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Editable state for overview fields
  const [overviewData, setOverviewData] = useState<Partial<UserService.User>>({})
  // Editable state for settings fields
  const [settingsData, setSettingsData] = useState<Partial<UserService.UserSettings>>({})

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminUserDetails', userId],
    queryFn: async () => {
      if (!userId) throw new Error('No userId')
      const response = await apis.userService.adminUserDetailsId(userId)
      return response.data
    },
    enabled: isOpen && !!userId,
  })

  // Initialize editable state when data loads
  useEffect(() => {
    if (data?.user) {
      setOverviewData({ ...data.user })
      setSettingsData({ ...data.user.settings })
    }
    setSaveError(null)
    setIsSaving(false)
  }, [data])

  const handleOverviewChange = useCallback((field: keyof UserService.User, value: unknown) => {
    setOverviewData(prev => ({ ...prev, [field]: value }))
  }, [])

  const handlePersonalInfoChange = useCallback((field: string, value: unknown) => {
    setOverviewData(prev => ({
      ...prev,
      personalInfo: { ...prev.personalInfo, [field]: value },
    }))
  }, [])

  const handleSettingsChange = useCallback((field: keyof UserService.UserSettings, value: unknown) => {
    setSettingsData(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleSave = async () => {
    if (!userId || !data?.user) return
    setSaveError(null)

    if (!overviewData.email || !overviewData.firstName || !overviewData.lastName) {
      setSaveError('Email, first name, and last name are required.')
      return
    }
    if (!overviewData.accessGroups || overviewData.accessGroups.length === 0) {
      setSaveError('At least one access group is required.')
      return
    }

    try {
      setIsSaving(true)
      const updateData: UserService.UpdateUserRequest = {
        email: overviewData.email,
        firstName: overviewData.firstName,
        lastName: overviewData.lastName,
        role: overviewData.role as UserService.UserRole,
        organizationId: overviewData.organizationId,
        isActive: overviewData.isActive,
        isVerified: overviewData.isVerified,
        accessGroups: overviewData.accessGroups as string[],
        notes: overviewData.notes,
        badges: overviewData.badges,
        source: overviewData.source,
        personalInfo: overviewData.personalInfo,
        settings: settingsData,
      }
      await apis.userService.idPut(userId, updateData)
      onUserSaved?.()
      onClose()
    } catch (error: unknown) {
      console.error('Failed to save user:', error)
      const errorMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to save user. Please try again.'
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isSaving && !open && onClose()}
    >
      <DialogContent
        className="sm:max-w-4xl lg:max-w-5xl sm:min-w-[600px] lg:min-w-[800px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => isSaving && e.preventDefault()}
        onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
        onInteractOutside={(e) => isSaving && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-[28px] italic font-medium text-primary/80">
            {data?.user
              ? `${data.user.firstName} ${data.user.lastName}`
              : t('users.userDetails')}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <UserManagementDialogSkeleton />
        )}

        {isError && (
          <div className="p-4 text-error text-sm">
            Failed to load user details.
          </div>
        )}

        {data && !isLoading && (
          <>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">{t('common.overview')}</TabsTrigger>
                <TabsTrigger value="assignments">{t('partner.assignments')}</TabsTrigger>
                <TabsTrigger value="challenges">{t('navigation.challenges')}</TabsTrigger>
                <TabsTrigger value="settings">{t('common.settings')}</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <OverviewSection
                  formData={overviewData}
                  onChange={handleOverviewChange}
                  onPersonalInfoChange={handlePersonalInfoChange}
                  allAccessGroups={allAccessGroups}
                  user={data.user}
                  challenges={data.challenges}
                />
              </TabsContent>

              <TabsContent value="assignments" className="mt-4">
                <AssignmentsSection assignments={data.assignments} />
              </TabsContent>

              <TabsContent value="challenges" className="mt-4">
                <ChallengesSection challenges={data.challenges} />
              </TabsContent>

              <TabsContent value="settings" className="mt-4">
                <SettingsSection
                  settingsData={settingsData}
                  onChange={handleSettingsChange}
                />
              </TabsContent>
            </Tabs>

            {saveError && (
              <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                <p className="text-sm text-error">{saveError}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4 justify-end">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-md hover:bg-secondary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isSaving ? t('common.saving') : t('users.updateUser')}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
